import { Injectable } from '@angular/core';
import { InveniasNamespace } from 'datasource-service-lib';
import { EntityControllerService } from 'entity-picker-lib';
import { IDataSourceApiService, IEntity, IEntityProfile, IRecentActivityEntityProfile } from 'interfaces-lib';
import { Observable, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { IRecentActivityProfileService } from '../../common-components/recent-activity/irecentactivityprofileservice';
import { ServiceProviderService } from '../service.provider.service';

@Injectable({
  providedIn: 'root',
})
export class InveniasRecentActivityProfileService implements IRecentActivityProfileService {
  private apiService: IDataSourceApiService;
  private mappers: { [index: string]: (item) => IEntityProfile } = {
    people: this.peopleMapper.bind(this),
    companies: this.companiesMapper.bind(this),
    programmes: this.programmesMapper.bind(this),
    assignments: this.assignmentsMapper.bind(this),
  };
  private static ERROR_MAPPER = () => {
    throw new Error('Unexpected profile type.');
  };
  private static CIRCLE_IMAGE_INDEX = 12;
  private static BLURRED_IMAGE_INDEX = 13;

  private entityProfileSelectedSubject = new ReplaySubject<IRecentActivityEntityProfile>();
  recentActivityEntityProfileSelected$ = this.entityProfileSelectedSubject.asObservable();

  constructor(private entityControllerService: EntityControllerService, sps: ServiceProviderService) {
    this.apiService = sps.getDataSourceApiService(InveniasNamespace.name);
    this.entityControllerService.entitySelected$.subscribe((entity) => {
      this.getRecentActivityProfile(entity).subscribe((data) => this.entityProfileSelectedSubject.next(data));
    });
  }

  getRecentActivityProfile(entity: IEntity): Observable<IRecentActivityEntityProfile> {
    const mapFn = this.mappers[entity.type] || InveniasRecentActivityProfileService.ERROR_MAPPER;
    return this.apiService.get(entity).pipe(map((fullEntity) => mapFn(fullEntity)));
  }

  private peopleMapper(entityData: any): IRecentActivityEntityProfile {
    return {
      id: entityData.id,
      type: entityData.type,
      name: entityData.NameComponents.FullName,
      profilePic: entityData.RecordPictures[InveniasRecentActivityProfileService.CIRCLE_IMAGE_INDEX].Uri,
      profilePicBlurred: entityData.RecordPictures[InveniasRecentActivityProfileService.BLURRED_IMAGE_INDEX].Uri,
      companyName: entityData.DefaultPosition.Company?.ItemDisplayText,
    };
  }

  private companiesMapper(entityData: any): IRecentActivityEntityProfile {
    return {
      id: entityData.id,
      type: entityData.type,
      name: entityData.CompanyName,
      profilePic: entityData.RecordPictures[InveniasRecentActivityProfileService.CIRCLE_IMAGE_INDEX].Uri,
      profilePicBlurred: entityData.RecordPictures[InveniasRecentActivityProfileService.BLURRED_IMAGE_INDEX].Uri,
    };
  }

  private programmesMapper(entityData: any): IRecentActivityEntityProfile {
    return {
      id: entityData.id,
      type: entityData.type,
      name: entityData.ProgrammeName,
    };
  }

  private assignmentsMapper(entityData: any): IRecentActivityEntityProfile {
    return {
      id: entityData.id,
      type: entityData.type,
      name: entityData.AssignmentName,
      companyName: entityData.Company.ItemDisplayText,
    };
  }
}
